import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import type { Episode, Season, SeasonAccess } from '../types';
import SeasonTitle from '../components/SeasonTitle';
import { formatDateRange, formatDuration, formatFileSizeLabel } from '../lib/format';

export default function SeasonPage() {
  const { year } = useParams();
  const navigate = useNavigate();
  const [episodes, setEpisodes] = useState<Episode[] | null>(null);
  const [season, setSeason] = useState<Season | null>(null);
  const [error, setError] = useState<string | null>(null);
  // ?owner= : a season someone shared with the signed-in user.
  const [search] = useSearchParams();
  const owner = search.get('owner');
  const [access, setAccess] = useState<SeasonAccess | null>(null);

  useEffect(() => {
    if (!year) return;
    let cancelled = false;
    api
      .season(Number(year), owner)
      .then((res) => {
        if (cancelled) return;
        setEpisodes(res.episodes);
        setSeason(res);
        setAccess(res.access ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [year, owner]);

  if (error) {
    return (
      <div className="mx-auto max-w-xl px-6 py-32 text-center">
        <h1 className="mb-2 text-2xl font-bold">Season {year}</h1>
        <p className="text-white/55">{error}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1600px] px-4 py-10 sm:px-6 lg:px-10">
      <button
        onClick={() => navigate(-1)}
        className="focus-ring mb-6 flex items-center gap-2 rounded text-sm text-white/50 transition hover:text-white"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Back
      </button>

      <header className="mb-10">
        {season ? (
          <SeasonTitle
            season={season}
            owner={access && !access.isOwner ? access.ownerId : null}
            canEdit={!access || access.role === 'OWNER' || access.role === 'EDITOR'}
            onChange={(updated) => setSeason({ ...season, ...updated })}
          />
        ) : (
          <div className="skeleton h-12 w-72" />
        )}
        <p className="mt-2 text-white/55">
          {access && !access.isOwner ? 'Shared with you' : (season?.description ?? `Your memories from ${year}`)}
        </p>
      </header>

      {!episodes ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="skeleton h-28 w-full" />
          ))}
        </div>
      ) : episodes.length === 0 ? (
        <p className="py-16 text-center text-white/40">No stories in this season yet.</p>
      ) : (
        <ol className="space-y-3">
          {episodes.map((ep, i) => (
            <li key={ep.id} className="animate-slide-up" style={{ animationDelay: `${i * 50}ms` }}>
              <EpisodeRow episode={ep} index={i} />
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** Large horizontal row — the season-page presentation from the spec. */
function EpisodeRow({ episode, index }: { episode: Episode; index: number }) {
  return (
    <a
      href={`/episode/${episode.id}`}
      className="focus-ring group flex gap-4 rounded-xl bg-ink-900 p-3 ring-1 ring-white/5 transition-all duration-300 hover:bg-ink-850 hover:ring-white/15 sm:gap-5 sm:p-4"
    >
      <div className="relative aspect-video w-32 shrink-0 overflow-hidden rounded-lg bg-ink-800 sm:w-52">
        {episode.coverUrl ? (
          <img
            src={episode.coverUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-ink-800 to-ink-950" />
        )}
        <span className="absolute top-1.5 left-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold">
          {index + 1}
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-center py-1">
        <h2 className="mb-1 truncate text-base font-semibold sm:text-lg">{episode.title}</h2>
        <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs text-white/50">
          <span>{formatDateRange(episode.startDate, episode.endDate)}</span>
          <span className="text-white/20">•</span>
          <span>{formatFileSizeLabel(episode.mediaCount, episode.videoCount)}</span>
          {episode.durationSeconds > 0 && (
            <>
              <span className="text-white/20">•</span>
              <span>{formatDuration(episode.durationSeconds)}</span>
            </>
          )}
        </div>
        {episode.description && (
          <p className="line-clamp-2 hidden text-xs leading-relaxed text-white/40 sm:block">
            {episode.description}
          </p>
        )}
      </div>

      <div className="hidden shrink-0 items-center pr-2 sm:flex">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 transition group-hover:bg-accent">
          <svg viewBox="0 0 24 24" className="ml-0.5 h-5 w-5 text-white" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
      </div>
    </a>
  );
}